import React, { useEffect } from 'react'
import {
    FormattedHTMLMessage,
    AppSvg,
    Spinner,
    FormattedCurrency,
    newIcons,
    ButtonVertical,
    Gapped,
    Button,
} from '@web/walter-ui-kit'

import { GetCurrenciesReturn, SwapTransaction, Transaction } from '@web/walter-lib/api'
import { logPosthogEvent } from '@walter/shared/utils/posthog'
import { rpPosthogEvents } from '@walter/shared/constants/posthog'
import { SwapOrConvertTransaction } from '..'
import styles from './style.module.css'
import { lang } from './lang'
import { generateUrl } from '~utils/navigation/generateUrl'
import { Routes } from '~constants/routes'
import { useModal } from '~contexts/ModalsContext'
import { ModalTypes } from '~contexts/ModalsContext/types'
import { getPrecision } from '~utils/getPrecision'

interface StatusHeaderProps {
    status: SwapTransaction['status']
}
interface Props {
    onClose: () => void
    transaction: SwapOrConvertTransaction
    tx: Transaction | null
    currencies: GetCurrenciesReturn | undefined
}

function SwapStatusHeader({ status }: StatusHeaderProps) {
    let title
    let icon

    if (status === 'SUCCESS') {
        title = lang.statusSuccess
        icon = (
            <div className={styles.success}>
                <AppSvg size={80} svgName={newIcons.ic80Check} path="new-icons" />
            </div>
        )
    } else if (status === 'FAILED' || status === 'ROLLED_BACK') {
        title = lang.statusFailed
        icon = (
            <div className={styles.failed}>
                <AppSvg size={80} svgName={newIcons.ic80CloseCircle} path="new-icons" />
            </div>
        )
    } else {
        title = lang.statusPending
        icon = <Spinner intent="warning" size={80} />
    }

    return (
        <div className={styles.header}>
            {icon}
            <FormattedHTMLMessage className={styles.statusTitle} {...title} />
        </div>
    )
}

// eslint-disable-next-line max-lines-per-function
function SwapTransactionStatus({ tx, transaction, currencies, onClose }: Props) {
    const { show } = useModal(ModalTypes.transactionDetails)

    const isFailed = transaction?.status === 'FAILED'

    const to = generateUrl({
        route: Routes.swap,
        fromCurrency: transaction.from_currency,
        toCurrency: transaction.to_currency,
        amount: transaction.from_amount,
    })

    const fromCurrency = transaction.from_currency
    const fromAmount = transaction.from_amount
    const toCurrency = transaction.to_currency
    const toAmount = transaction.to_amount
    const fromCurrencyPrecision = getPrecision(currencies?.[fromCurrency ?? '']?.precision_transfer)
    const toCurrencyPrecision = getPrecision(currencies?.[toCurrency ?? '']?.precision_transfer)

    useEffect(() => {
        if (['PENDING', 'SUCCESS', 'FAILED'].includes(transaction.status || '')) {
            const getPosthogEvent = () => {
                switch (transaction.status) {
                    case 'PENDING':
                        return rpPosthogEvents.swapFlowStatusPending
                    case 'SUCCESS':
                        return rpPosthogEvents.swapFlowStatusSuccess
                    default:
                        return rpPosthogEvents.swapFlowStatusFailed
                }
            }

            logPosthogEvent(getPosthogEvent(), {
                from_amount: fromAmount,
                from_currency: fromCurrency,
                to_amount: toAmount,
                to_currency: toCurrency,
            })
        }
    }, [transaction.status, fromAmount, fromCurrency, toCurrency, toAmount])

    return (
        <div data-pointer="swap_transaction_status" className={styles.wrap}>
            <SwapStatusHeader status={transaction.status} />

            <div className={styles.details}>
                <FormattedCurrency
                    full
                    value={fromAmount}
                    currency={fromCurrency}
                    dataPointer="from_amount"
                    maxPrecision={fromCurrencyPrecision}
                />
                <AppSvg size={20} svgName={newIcons.ic20ArrowRight} path="new-icons" />
                <FormattedCurrency
                    full
                    value={toAmount}
                    currency={toCurrency}
                    dataPointer="to_amount"
                    maxPrecision={toCurrencyPrecision}
                />
            </div>

            <div className={styles.gapped}>
                <Gapped dataPointer="action_buttons" gap={32}>
                    <ButtonVertical
                        role="button"
                        isDisabled={!tx}
                        dataPointer="transaction_details_button"
                        onClick={() => {
                            tx && show({ tx, onActionClick: onClose })
                        }}
                        label={<FormattedHTMLMessage {...lang.detailsButton} />}
                        icon={<AppSvg path="new-icons" svgName={newIcons.ic24News} size={24} />}
                    />
                    {!isFailed && (
                        <ButtonVertical
                            to={to}
                            role="link"
                            onClick={onClose}
                            dataPointer="repeat_swap_button"
                            label={<FormattedHTMLMessage {...lang.repeatButton} />}
                            icon={<AppSvg path="new-icons" svgName={newIcons.ic24Refresh} size={24} />}
                        />
                    )}
                </Gapped>
            </div>

            <div className={styles.actionButtons}>
                {isFailed && (
                    <Button
                        to={to}
                        size="l"
                        fullWidth
                        role="link"
                        mode="primary"
                        onClick={onClose}
                        dataPointer="try_again_button"
                    >
                        <FormattedHTMLMessage {...lang.tryAgainButton} />
                    </Button>
                )}
                <Button
                    size="l"
                    fullWidth
                    onClick={onClose}
                    mode={isFailed ? 'secondary' : 'primary'}
                    dataPointer="close_transaction_status_button"
                >
                    <FormattedHTMLMessage {...lang.closeButton} />
                </Button>
            </div>
        </div>
    )
}

export default SwapTransactionStatus
